/**
 * POS Mobile (Tablet-first PWA) — Garson uygulaması
 *
 * Login → Kasa Aç → Bottom Nav (Masalar / QR / Rezerv. / Profil)
 *
 * Sipariş + ödeme ayrı sekme DEĞİL: masaya basınca açılan birleşik masa
 * ekranında ele alınır (boş masada ürün seçimi, dolu masada açık sipariş
 * özeti + Hesap Al). "Masalar" sekmesi ızgara ↔ masa ekranı arasında geçer.
 */
import { useEffect, useState } from "react";
import { AppShell, Tabs, Center, Loader, Alert, Badge, Box, Group, UnstyledButton, Avatar, Text, ActionIcon } from "@mantine/core";
import {
  IconLayoutGrid,
  IconQrcode,
  IconWifiOff,
  IconCalendar,
  IconLock,
  IconBell,
  IconChevronLeft,
  IconCash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../shared/auth";
import { useOfflineQueue } from "../../shared/pwa";
import {
  useCashierClosingState,
  useMyOpenShift,
  useQrPendingOrders,
  useQrPendingRequests,
  useReadyKdsTickets,
} from "../../shared/api";
import { useWebSocket } from "../../shared/ws";
import { startPrintQueueWorker, startHeartbeat } from "../../shared/hardware";
import { LoginPage } from "./pages/Login";
import { ShiftOpenPage } from "./pages/ShiftOpen";
import { ShiftClosedNotice } from "./pages/ShiftClosedNotice";
import { TablesPage } from "./pages/Tables";
import { TableScreen } from "./pages/TableScreen";
import { ReadyTicketsPage } from "./pages/ReadyTickets";
import { QrApprovalPage } from "./pages/QrApproval";
import { POSReservationsPage } from "./pages/Reservations";
import { ProfilePage } from "./pages/Profile";

// Masa durumu → header rozeti etiketi + rengi. TableScreen aynı sözlüğü
// kullanır; masa açıkken header'da "POS"un sağına ortalanan durum rozeti
// (BOŞ / DOLU / REZERVE ...) buradan beslenir.
const STATE_LABEL: Record<string, string> = {
  FREE: "BOŞ",
  OCCUPIED: "DOLU",
  RESERVED: "REZERVE",
  CLEANING: "TEMİZLİK",
  OUT_OF_SERVICE: "SERVİS DIŞI",
};
const STATE_COLOR: Record<string, string> = {
  FREE: "green",
  OCCUPIED: "red",
  RESERVED: "yellow",
  CLEANING: "gray",
  OUT_OF_SERVICE: "dark",
};

// Masa ekranı açıkken header'a taşınan masa bilgisi. TableScreen onHeaderInfo
// ile bildirir; masadan çıkınca null'a döner (geri ok + durum rozeti gizlenir).
export type PosTableHeader = {
  name: string;
  state: string;
  paymentRequested: boolean;
};

export function App() {
  const { isAuthenticated, isLoading, user, role } = useAuth();
  // Şube başına tek kasa: kasayı yalnız kasiyer/yönetici açar. Garson açamaz —
  // açık kasa varsa ona yazar, yoksa "kasa açılmadı" bilgisi görür.
  const canOpenShift =
    role === "OWNER" || role === "MANAGER" || role === "CASHIER";
  const [shiftOpen, setShiftOpen] = useState(false);
  const [tab, setTab] = useState<string>("tables");
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  // Masa ekranı açıkken TableScreen'in bildirdiği masa bilgisi — header'daki
  // geri ok + ortalanmış durum rozetini besler.
  const [tableHeader, setTableHeader] = useState<PosTableHeader | null>(null);
  // Masa kapanınca (geri / sekme değişimi / masadan çıkış) header bilgisini
  // temizle — aksi halde ızgaraya dönünce "POS"un yanında eski rozet asılı kalır.
  useEffect(() => {
    if (selectedTableId === null) setTableHeader(null);
  }, [selectedTableId]);
  const { online, pendingCount } = useOfflineQueue();
  const { data: myShift, isLoading: shiftLoading, refetch: refetchMyShift } = useMyOpenShift({ enabled: isAuthenticated });
  useEffect(() => {
    if (myShift?.shift?.is_open) setShiftOpen(true);
  }, [myShift]);
  const { data: closing } = useCashierClosingState({ enabled: isAuthenticated && shiftOpen });
  const isClosing = closing?.closing_state === true && closing?.pause_enabled !== false;

  // QR sekmesi bildirim rozeti — garson onayı bekleyen QR sipariş sayısı.
  // İKİ kaynak: eski DRAFT Order'lar (useQrPendingOrders) + yeni mimari
  // PendingQrRequest'ler (useQrPendingRequests). Rozet bunların TOPLAMINI
  // gösterir; aksi halde yeni mimaride gelen sipariş rozete yansımaz ve
  // garson masaya düşmediğini sanır (App seviyesinde çağrılır ki QR Onay
  // sayfası kapalıyken de — örn. Masa Planı'ndayken — sayı canlı kalsın).
  // enabled: login öncesi çağrılmamalı — token yokken 401 → reload döngüsü.
  const { data: qrPending } = useQrPendingOrders({ enabled: isAuthenticated });
  const { data: qrRequests } = useQrPendingRequests({ enabled: isAuthenticated });
  const legacyCount =
    (Array.isArray(qrPending) ? qrPending : qrPending?.results)?.length ?? 0;
  const requestCount =
    (Array.isArray(qrRequests) ? qrRequests : qrRequests?.results)?.length ?? 0;
  const qrPendingCount = legacyCount + requestCount;

  // Bu POS terminali bir hardware device olarak kayıtlı ise
  // print queue worker'ı + heartbeat'i başlat. device_id localStorage'da
  // (admin tarafından device kayıt edilince app burada pair edilir).
  useEffect(() => {
    const deviceId = localStorage.getItem("pos_device_id");
    if (!deviceId || !isAuthenticated || !shiftOpen) return;
    const stopHb = startHeartbeat(deviceId);
    const stopWorker = startPrintQueueWorker(deviceId);
    return () => { stopHb(); stopWorker(); };
  }, [isAuthenticated, shiftOpen]);

  // Global WS — garson hangi sekmede olursa olsun "Sipariş Hazır" toast'ı
  // göster + tables cache invalidate (Hazır rozeti masa kartında belirsin).
  // App seviyesinde tek subscription — Tables/QrApproval sayfalarındaki ek
  // listener'lar duplicate toast yaratırdı, oradan kaldırıldı.
  const qc = useQueryClient();
  const { lastMessage: kdsMessage } = useWebSocket<any>(
    isAuthenticated && shiftOpen ? "/ws/waiters/" : null,
  );
  useEffect(() => {
    if (!kdsMessage) return;
    if (kdsMessage.event === "kds.ticket.ready") {
      const tableName = kdsMessage.table_name || "Masa";
      // Siparişin garsonu kim? Eşleşirse büyük alarm; başkasının masası
      // ise küçük bilgilendirme. Sipariş garson atanmamışsa (gel-al/paket)
      // herkes eşit aday — büyük alarm.
      const myUserId = user?.id ?? null;
      const ticketWaiterId = kdsMessage.waiter_id ?? null;
      const isMine =
        ticketWaiterId === null ||
        (myUserId !== null && Number(myUserId) === Number(ticketWaiterId));

      if (isMine) {
        notifications.show({
          title: "🔔 Sipariş HAZIR",
          message: `${tableName} — mutfaktan al ve servis et`,
          color: "green",
          autoClose: false,
        });
      } else {
        const waiterName = kdsMessage.waiter_name || "başka garson";
        notifications.show({
          message: `Hazır: ${tableName} (${waiterName})`,
          color: "gray",
          autoClose: 4000,
        });
      }
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["kds-tickets"] });
    }

    // KDS chef "Servis Edildi" basınca → POS masa drawer'ı + admin
    // Siparişler tablosu anlık tazelensin. Daha önce sadece istasyon
    // grubuna gidiyordu; POS 10sn refetchInterval'ı bekleyerek geç
    // gösteriyordu (kullanıcı şikayetinin sebebi). Toast YOK — bu sessiz
    // bir cache invalidate; garson kendi "Servis Edildi" basınca zaten
    // POS açık, anlık güncellemek yeterli.
    if (kdsMessage.event === "kds.ticket.served") {
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["kds-tickets"] });
    }

    // Order.kitchen_status değişti (PREPARING/READY/SERVED) —
    // _sync_order_kitchen_status yayını. Admin Siparişler tablosu + POS
    // drawer cache invalidate. WS bağlıyken refetchInterval'a bağımlı
    // kalmaz; refresh sırasında "Onaylandı" olarak takılı kalmaz.
    if (kdsMessage.event === "order.status_changed") {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    }

    // Sipariş iptal edildi — admin/POS listede İPTAL rozeti anlık görünsün.
    if (kdsMessage.event === "order.cancelled") {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    }

    // Gün sonu kapanış kilidi değişti — POS banner'ı ANINDA aç/kapan.
    // Önceden WS push yoktu; banner sadece 30sn polling ile güncelleniyordu,
    // bu yüzden yönetici "Z Tamamla" deyince POS'ta banner takılı görünüyordu.
    if (
      kdsMessage.event === "cashier.closing.started" ||
      kdsMessage.event === "cashier.closing.completed" ||
      kdsMessage.event === "cashier.closing.cancelled"
    ) {
      qc.invalidateQueries({ queryKey: ["cashier-closing-state"] });
      if (kdsMessage.event === "cashier.closing.started") {
        notifications.show({
          title: "🔒 Gün Sonu Kapanış Başladı",
          message: "Yeni sipariş kabulü duraklatıldı — Z raporu tamamlanınca açılır.",
          color: "orange",
        });
      } else {
        notifications.show({
          title: "✅ Sipariş Kabulü Açık",
          message: "Gün sonu kapanışı tamamlandı — yeni sipariş alabilirsiniz.",
          color: "green",
        });
      }
    }

    // QR'dan yeni sipariş geldiğinde garson HER SAYFADA görsün — eski
    // dinleyici sadece QrApproval sayfası açıkken çalışıyordu, garson
    // başka sekmedeyken bildirimi kaçırıyordu (POS'a "geç düşüyor"
    // şikayetinin asıl sebebi: hiç düşmüyordu, sadece QR Onay sayfasına
    // geçince listede görülüyordu). Tables sayfası da bu eventte tables
    // cache'i invalidate ediyor; burası onun yerine geçmiyor, sadece
    // toast'ı garantiliyor (App seviyesi her sekme için tek subscription).
    if (kdsMessage.event === "qr.order.pending_approval") {
      const tableName = kdsMessage.table_name || "Masa";
      notifications.show({
        title: "🔔 Yeni QR Sipariş",
        message: `${tableName} — onay bekliyor`,
        color: "yellow",
        autoClose: false,
      });
      qc.invalidateQueries({ queryKey: ["qr-pending"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    }

    // Yeni mimari — PendingQrRequest (Order yaratılmadan müşteri isteği).
    // Eski qr.order.pending_approval ile aynı garson deneyimi: her sayfada
    // toast + qr-requests-pending cache invalidate → QR rozeti anlık artar.
    if (kdsMessage.event === "qr.request.pending_approval") {
      const tableName = kdsMessage.table_name || "Masa";
      notifications.show({
        title: "🔔 Yeni QR Sipariş",
        message: `${tableName} — onay bekliyor`,
        color: "yellow",
        autoClose: false,
      });
      // refetchType: "all" — rozet query'si WS bağlıyken background sayılabilir;
      // düz invalidate refetch tetiklemeyebilir (sayfa değişene dek geç güncellenir).
      qc.invalidateQueries({
        queryKey: ["qr-requests-pending"], refetchType: "all",
      });
      qc.invalidateQueries({ queryKey: ["tables"] });
    }
    // Request onaylandı/reddedildi/iptal edildi — rozet ve liste düşsün.
    if (
      kdsMessage.event === "qr.request.approved" ||
      kdsMessage.event === "qr.request.rejected" ||
      kdsMessage.event === "qr.request.cancelled"
    ) {
      qc.invalidateQueries({
        queryKey: ["qr-requests-pending"], refetchType: "all",
      });
      qc.invalidateQueries({ queryKey: ["tables"] });
    }

    // Hesap aktarımı — hedef masaya satır geldiğinde garson bilsin.
    // Kaynak masanın garsonu modal'da zaten başarı toast'ı görür; bu
    // toast hedef masaya bakan garson(lar) için.
    if (kdsMessage.event === "order.lines_transferred") {
      const sourceName = kdsMessage.source_table_name || "başka bir masa";
      const targetName = kdsMessage.target_table_name || "bir masa";
      const count = kdsMessage.line_count || 0;
      notifications.show({
        title: "🔀 Hesap Aktarıldı",
        message: `${sourceName} → ${targetName}: ${count} satır`,
        color: "blue",
        autoClose: 6000,
      });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    }
  }, [kdsMessage, qc, user?.id]);

  // Hazır sayacı — bottom nav rozeti için. WS push olduğunda yukarıdaki
  // invalidate tetiklediği için bu hook hemen taze veri çeker.
  const { data: readyData } = useReadyKdsTickets({
    enabled: isAuthenticated && shiftOpen,
    wsConnected: true,  // bu sayfa zaten WS abone — polling kapalı kalsın
  });
  const readyCount = (
    Array.isArray(readyData) ? readyData : readyData?.results
  )?.length ?? 0;

  if (isLoading || (isAuthenticated && shiftLoading)) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!isAuthenticated) return <LoginPage />;
  if (!shiftOpen) {
    // Kasa açabilen rol → açılış formu. Garson gibi yetkisiz rol →
    // "kasiyer/yönetici kasayı açsın" bilgi ekranı (sipariş ekranına giremez,
    // çünkü nakit satış açık kasa olmadan ledger'a yazılamaz).
    return canOpenShift ? (
      <ShiftOpenPage onOpened={() => setShiftOpen(true)} />
    ) : (
      <ShiftClosedNotice onRetry={() => refetchMyShift()} />
    );
  }

  // Garsonun ekran adı — username veya full name
  const userLabel = user?.username || user?.email || "Garson";
  const userInitial = (userLabel[0] || "G").toUpperCase();

  return (
    <AppShell
      header={{ height: 52 }}
      footer={{ height: 70 }}
      padding="md"
    >
      {/* Sağ üst köşe — garson avatarı, tıklanınca Profil/Ayarlar açılır.
          Avatar = küçük + sade; sürekli ekranda yer kaplamasın. Username
          dar ekranlarda gizlenir (xs breakpoint), sadece avatar görünür. */}
      <AppShell.Header
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          background: "var(--mantine-color-body)",
        }}
      >
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="xs">
          {/* Sol grup — masa açıkken geri ok "POS"un soluna gelir, durum
              rozeti (BOŞ/DOLU...) "POS"un sağına ortalanır. Masada değilken
              sadece "POS" yazısı görünür. */}
          <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            {tableHeader && (
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => setSelectedTableId(null)}
                aria-label="Geri"
              >
                <IconChevronLeft size={22} />
              </ActionIcon>
            )}
            <Text fw={700} c="brand" style={{ flexShrink: 0 }}>🍽️ POS</Text>
            {tableHeader && (
              <Group justify="center" style={{ flex: 1, minWidth: 0 }}>
                {tableHeader.paymentRequested ? (
                  <Badge
                    color="grape"
                    size="lg"
                    variant="filled"
                    leftSection={<IconCash size={14} />}
                  >
                    Hesap İstiyor
                  </Badge>
                ) : (
                  <Badge color={STATE_COLOR[tableHeader.state] || "gray"} size="lg">
                    {STATE_LABEL[tableHeader.state] || tableHeader.state}
                  </Badge>
                )}
              </Group>
            )}
          </Group>
          <UnstyledButton
            onClick={() => setTab("profile")}
            aria-label="Profil ve ayarlar"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "4px 6px", borderRadius: 999,
              background: tab === "profile"
                ? "var(--mantine-color-blue-light)"
                : "transparent",
            }}
          >
            <Text size="sm" fw={500} visibleFrom="xs">{userLabel}</Text>
            <Avatar size={32} color="blue" radius="xl">
              {userInitial}
            </Avatar>
          </UnstyledButton>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        {!online && (
          <Alert color="orange" icon={<IconWifiOff size={18} />} mb="md" radius={0}>
            Çevrimdışı — {pendingCount > 0 && <Badge color="orange" ml="xs">{pendingCount} bekleyen</Badge>} Bağlantı gelince otomatik senkronlanacak
          </Alert>
        )}
        {isClosing && (
          <Alert
            color="red"
            icon={<IconLock size={18} />}
            mb="md"
            radius={0}
            title="🔒 Gün Sonu Kapanış"
          >
            Restoran yönetici "Gün Sonu Başlat" dedi — yeni sipariş onayı +
            yeni ödeme alma <strong>geçici olarak kilitli</strong>. Açık
            siparişleri kapatabilirsiniz; Z raporu tamamlanınca kilit
            otomatik açılır.
          </Alert>
        )}
        {tab === "tables" && selectedTableId === null && (
          <TablesPage onSelectTable={(id) => setSelectedTableId(id)} />
        )}
        {tab === "tables" && selectedTableId !== null && (
          <TableScreen
            tableId={selectedTableId}
            onBack={() => setSelectedTableId(null)}
            onHeaderInfo={setTableHeader}
          />
        )}
        {tab === "qr" && <QrApprovalPage />}
        {tab === "ready" && <ReadyTicketsPage />}
        {tab === "reservations" && <POSReservationsPage />}
        {tab === "profile" && <ProfilePage onCloseShift={() => setShiftOpen(false)} />}
      </AppShell.Main>

      <AppShell.Footer
        // iOS gesture bar / notch: footer'ı güvenli alanın üstünde tut
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <Tabs
          value={tab}
          onChange={(v) => {
            if (!v) return;
            // Sekme değişince masa ekranından ızgaraya dön
            if (v === "tables") setSelectedTableId(null);
            setTab(v);
          }}
          variant="default"
        >
          <Tabs.List grow style={{ height: 70 }}>
            <Tabs.Tab value="tables" leftSection={<IconLayoutGrid size={22} />}>
              Masalar
            </Tabs.Tab>
            <Tabs.Tab
              value="qr"
              leftSection={
                // Rozet ikonun sağ-üstüne mutlak konumlanır — tab içeriğini
                // büyütmez, footer satır yüksekliği sabit kalır.
                <Box pos="relative" style={{ display: "inline-flex" }}>
                  <IconQrcode size={22} />
                  {qrPendingCount > 0 && (
                    <Badge
                      size="sm"
                      circle
                      variant="filled"
                      color="red"
                      style={{
                        position: "absolute",
                        top: -8,
                        right: -10,
                        pointerEvents: "none",
                      }}
                    >
                      {qrPendingCount}
                    </Badge>
                  )}
                </Box>
              }
            >
              QR
            </Tabs.Tab>
            <Tabs.Tab
              value="ready"
              leftSection={
                <Box pos="relative" style={{ display: "inline-flex" }}>
                  <IconBell size={22} />
                  {readyCount > 0 && (
                    <Badge
                      size="sm"
                      circle
                      variant="filled"
                      color="green"
                      style={{
                        position: "absolute",
                        top: -8,
                        right: -10,
                        pointerEvents: "none",
                      }}
                    >
                      {readyCount}
                    </Badge>
                  )}
                </Box>
              }
            >
              Hazır
            </Tabs.Tab>
            <Tabs.Tab value="reservations" leftSection={<IconCalendar size={22} />}>
              Rezerv.
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </AppShell.Footer>
    </AppShell>
  );
}
